import { Check, ChevronDown, Search } from "lucide-react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command"
import { cn } from "@/lib/utils"
import { Toggle } from "@/components/ui/toggle2"
import { Switch } from "@/components/ui/switch"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { UNASSIGNED_SECTION_JID } from "@/components/chatList/dispatcherSections"
import { useTranslation } from "react-i18next"

const ALL_DISPATCHERS = "all"

export type DriverFilter =
  | "driving"
  | "not-driving"
  | "on-route"
  | "off-route"
  | "with-delay"

export type FilterConfig = {
  selected: DriverFilter[]
  showAllCars: boolean
  // Trailers are a separate map layer with no chat behind them; this only
  // shows or hides that layer and never affects the driver list.
  showTrailers: boolean
  /** A fleet manager's pick: one dispatcher's vehicles, the unassigned ones
   * (UNASSIGNED_SECTION_JID), or everyone's when empty. */
  dispatcherJid?: string
}

export type DispatcherFilterOption = { jid: string; label: string }

type SearchBarProps = {
  searchQuery: string
  onSearchChange: (query: string) => void
  filterConfig: FilterConfig
  onFilterChange: (config: FilterConfig) => void
  onFilterClick?: () => void
  isFilterOpen: boolean
  /** Shown only to a fleet manager; empty hides the dispatcher picker. */
  dispatchers?: DispatcherFilterOption[]
}

export default function SearchBar({
  searchQuery,
  onSearchChange,
  filterConfig,
  onFilterChange,
  isFilterOpen,
  onFilterClick,
  dispatchers = [],
}: SearchBarProps) {
  const { t } = useTranslation()
  const filterOptions = [
    { value: "driving", label: t("fleet.fleetOverview.filters.driving") },
    {
      value: "not-driving",
      label: t("fleet.fleetOverview.filters.notDriving"),
    },
    { value: "on-route", label: t("fleet.fleetOverview.filters.onRoute") },
    { value: "off-route", label: t("fleet.fleetOverview.filters.offRoute") },
    { value: "with-delay", label: t("fleet.fleetOverview.filters.withDelay") },
    { value: "with-stops", label: t("fleet.fleetOverview.filters.withStops") },
    {
      value: "without-stops",
      label: t("fleet.fleetOverview.filters.withoutStops"),
    },
  ]

  const handleFilterSelect = (value: DriverFilter) => {
    const newSelected = filterConfig.selected.includes(value)
      ? filterConfig.selected.filter((item) => item !== value)
      : [...filterConfig.selected, value]
    onFilterChange({ ...filterConfig, selected: newSelected })
  }

  const handleShowAllCarsChange = (checked: boolean) => {
    onFilterChange({ ...filterConfig, showAllCars: checked })
  }

  const handleShowTrailersChange = (checked: boolean) => {
    onFilterChange({ ...filterConfig, showTrailers: checked })
  }

  const handleDispatcherChange = (dispatcherJid: string) => {
    onFilterChange({
      ...filterConfig,
      dispatcherJid:
        dispatcherJid === ALL_DISPATCHERS ? undefined : dispatcherJid,
    })
  }

  const handleEscapeKeyDown = (event: KeyboardEvent) => {
    // Prevent the default behavior of closing the popover
    event.preventDefault()
  }
  return (
    <div className="mb-1 flex items-center gap-3">
      <div className="relative mt-[1px] max-h-[52px] flex-1">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 transform text-muted-foreground" />
        <Input
          type="search"
          placeholder={t("fleet.fleetOverview.searchPlaceholder")}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-9 rounded-md bg-white pl-10 dark:bg-input/30"
        />
      </div>

      <Popover open={isFilterOpen} onOpenChange={onFilterClick}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="default"
            role="combobox"
            className={cn(
              "min-w-[80px] justify-between rounded-md border px-4 py-2 text-left font-medium hover:bg-blue-100 dark:hover:bg-blue-800",
            )}
          >
            <span className="truncate text-foreground">
              {t("fleet.fleetOverview.filter")}
              {filterConfig.selected.length > 0 && (
                <span> ({filterConfig.selected.length})</span>
              )}
            </span>
            <ChevronDown className="ml-4 mr-2.5 h-4 w-4 shrink-0 text-gray-500" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="z-[200] w-[220px] p-0"
          align="start"
          onEscapeKeyDown={handleEscapeKeyDown}
        >
          <Command>
            {/* A fleet manager keeps no cars of their own, so "my cars" is
                not a choice for them; the dispatcher picker below is. */}
            {dispatchers.length === 0 && (
              <div className="border-b p-3">
                <div className="flex items-center space-x-2">
                  <Toggle
                    className="flex-1"
                    name="show-all-cars"
                    onChange={(value) =>
                      handleShowAllCarsChange(value === "all")
                    }
                    defaultValue={filterConfig.showAllCars ? "all" : "mine"}
                  >
                    <Toggle.Control
                      label={t("fleet.fleetOverview.myCars")}
                      value="mine"
                    />
                    <Toggle.Control
                      label={t("fleet.fleetOverview.allCars")}
                      value="all"
                    />
                  </Toggle>
                </div>
              </div>
            )}
            {dispatchers.length > 0 && (
              <div className="flex items-center justify-between gap-2 border-b px-3 py-2">
                <Label
                  htmlFor="fleet-dispatcher"
                  className="text-sm font-normal"
                >
                  {t("fleet.fleetOverview.dispatcher")}
                </Label>
                <Select
                  value={filterConfig.dispatcherJid || ALL_DISPATCHERS}
                  onValueChange={handleDispatcherChange}
                >
                  <SelectTrigger
                    id="fleet-dispatcher"
                    className="h-8 w-[120px]"
                    data-testid="fleet-dispatcher-filter"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[210]">
                    <SelectItem value={ALL_DISPATCHERS}>
                      {t("fleet.fleetOverview.allDispatchers")}
                    </SelectItem>
                    {dispatchers.map((dispatcher) => (
                      <SelectItem key={dispatcher.jid} value={dispatcher.jid}>
                        {dispatcher.label}
                      </SelectItem>
                    ))}
                    <SelectItem value={UNASSIGNED_SECTION_JID}>
                      {t("fleet.fleetOverview.unassigned")}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center justify-between border-b px-3 py-2">
              <Label htmlFor="show-trailers" className="text-sm font-normal">
                {t("fleet.fleetOverview.showTrailers")}
              </Label>
              <Switch
                id="show-trailers"
                checked={filterConfig.showTrailers}
                onCheckedChange={handleShowTrailersChange}
              />
            </div>
            <CommandList>
              <CommandEmpty>{t("fleet.fleetOverview.noOptions")}</CommandEmpty>
              <CommandGroup>
                {filterOptions.map((option) => (
                  <CommandItem
                    key={option.value}
                    value={option.value}
                    onSelect={() =>
                      handleFilterSelect(option.value as DriverFilter)
                    }
                  >
                    {filterConfig.selected.includes(
                      option.value as DriverFilter,
                    ) && <Check className="mr-2 h-4 w-4" />}
                    {option.label}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
